import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideo } from '../../context/VideoContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiSearch } = FiIcons;

const VideoList = ({ onEdit }) => {
  const { videos, deleteVideo } = useVideo();

  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterTitle, setFilterTitle] = useState('');

  const filteredVideos = videos.filter(v => {
    if (filterTitle && !v.title.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    return true;
  });

  const getThumbnailUrl = (url) => {
    if (!url) return null;
    const youtubeRegex = new RegExp('(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})', 'i');
    const vimeoRegex = new RegExp('(?:vimeo\\.com/|player\\.vimeo\\.com/video/)([0-9]+)', 'i');
    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
    return null;
  };

  const sortedVideos = [...filteredVideos].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    if (sortBy === 'created_at' || sortBy === 'published_on') {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    }
    if (sortOrder === 'asc') return aValue > bValue ? 1 : -1;
    return aValue < bValue ? 1 : -1;
  });

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this video?')) deleteVideo(id);
  };

  const handleEditClick = (e, video) => {
    e.stopPropagation();
    onEdit(video);
  };

  const renderTypes = (video) => {
    const types = Array.isArray(video.video_type) ? video.video_type : [video.video_type];
    return types.filter(Boolean).join(', ');
  };

  const selectStyle = "w-full md:w-auto px-4 py-2 bg-zinc-900/50 border border-white/10 rounded-2xl text-white text-sm focus:border-orange-500 outline-none appearance-none custom-select";

  return (
    <div className="glass-card p-6 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <h3 className="text-2xl font-bold text-white">
          Projects{' '}
          <span className="text-sm font-normal text-slate-500 ml-2">({filteredVideos.length})</span>
        </h3>
        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
            <input
              type="text"
              value={filterTitle}
              onChange={(e) => setFilterTitle(e.target.value)}
              placeholder="Search by title..."
              className="w-full pl-10 pr-4 py-2 bg-zinc-900/50 border border-white/10 rounded-2xl text-white text-sm focus:border-orange-500 outline-none transition-all"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={selectStyle}
          >
            <option value="created_at">Date Added</option>
            <option value="title">Title</option>
            <option value="views">Views</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-secondary text-sm px-4"
          >
            {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>
      </div>

      {sortedVideos.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
          <p className="text-white/60 text-lg font-medium">No videos found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedVideos.map((video, index) => (
            <motion.div
              key={video.id}
              className="group bg-zinc-900/40 border border-white/5 rounded-2xl p-5 hover:border-orange-500/30 transition-all cursor-pointer hover:bg-zinc-900/60"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => onEdit(video)}
            >
              <div className="flex flex-col lg:flex-row gap-6 items-center">
                <div className="w-full lg:w-48 aspect-video rounded-xl bg-zinc-800 flex-shrink-0 relative overflow-hidden">
                  {getThumbnailUrl(video.video_link) ? (
                    <img
                      src={getThumbnailUrl(video.video_link)}
                      alt={video.title}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent" />
                  )}
                </div>

                <div className="flex-1 min-w-0 w-full">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-lg font-bold text-white truncate group-hover:text-orange-400 transition-colors uppercase tracking-tight">
                        {video.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        <span className="text-xs font-bold text-orange-500/80 bg-orange-500/10 px-2 py-0.5 rounded uppercase">
                          {video.industry}
                        </span>
                        <span className="text-xs text-slate-500 font-medium truncate max-w-[200px]">
                          {renderTypes(video)}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={(e) => handleEditClick(e, video)}
                        className="p-2.5 rounded-xl bg-white/5 hover:bg-blue-500/20 text-blue-400 transition-all border border-white/5"
                        title="Edit Details"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, video.id)}
                        className="p-2.5 rounded-xl bg-white/5 hover:bg-red-500/20 text-red-400 transition-all border border-white/5"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm mt-4">
                    <div>
                      <span className="text-white/30 text-[10px] font-bold uppercase tracking-widest block mb-1">Company</span>
                      <p className="text-white font-medium truncate">{video.client || 'Unknown'}</p>
                    </div>
                    <div>
                      <span className="text-white/30 text-[10px] font-bold uppercase tracking-widest block mb-1">Added</span>
                      <p className="text-white font-medium">{new Date(video.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoList;